"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useComputed = void 0;
const fast_deep_equal_1 = __importDefault(require("fast-deep-equal"));
const mobx_1 = require("mobx");
const react_1 = require("react");
/** Evaluates a computed function `fn` to a regular value and triggers a re-render whenever it changes. */
function useComputed(fn, deps) {
    // We always return the useRef value, and use this just to trigger re-renders
    const [, setTick] = (0, react_1.useState)(0);
    const ref = (0, react_1.useRef)({
        runner: undefined,
        value: undefined,
        hasRan: false,
    });
    // We use a `useMemo` b/c we want this to synchronously calc, so that even
    // the very 1st render can use the result of our computed, i.e. instead of
    // with `useEffect`, which would only get calc'd after the 1st render has
    // already been done.
    (0, react_1.useMemo)(() => {
        const { current } = ref;
        // If deps has changed, unhook the previous observer
        if (current.runner) {
            current.runner();
        }
        // If deps has changed, we're already re-running, so don't trigger a 2nd one
        current.hasRan = false;
        current.runner = (0, mobx_1.autorun)(() => {
            const { value: oldValue, hasRan: oldHasRun } = current;
            // Always eval fn() (even on 1st render) to register our observable.
            const newValue = fn(oldValue);
            current.value = newValue;
            current.hasRan = true;
            // Only trigger a re-render if this is not the 1st autorun. Note
            // that if deps has changed, we're inherently in a re-render so also
            // don't need to trigger an additional re-render.
            if (oldHasRun && !(0, fast_deep_equal_1.default)(newValue, oldValue)) {
                setTick((tick) => tick + 1);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);
    // unsubscribe the autorun when we're unmounted
    (0, react_1.useEffect)(() => {
        return ref.current.runner;
    }, []);
    // Occasionally autorun will not have run yet, in which case we have to just
    // accept running the eval fn twice (here to get the value for the 1st render,
    // and again for mobx to watch what observables we touch).
    if (!ref.current.hasRan) {
        ref.current.value = fn(undefined);
    }
    // We can use `!` here b/c we know that `autorun` set current
    return ref.current.value;
}
exports.useComputed = useComputed;
